import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import purchaseRequestService from "../../services/purchaseRequestService";
import financeService from "../../services/financeService";
import toast from "react-hot-toast";
import { useAuth } from "../../contexts/AuthContext";
import {
  LayoutGrid,
  List as ListIcon,
  BarChart3,
  Eye,
  Download,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
  RefreshCw,
  ExternalLink,
} from "lucide-react";
import PurchaseReceipt from "./PurchaseReceipt";
import jsPDF from "jspdf";
import { PDFDocument, rgb } from "pdf-lib";
import useNotificationRefresh from "../../hooks/useNotificationRefresh";

const FinancePayment = () => {
  const { user } = useAuth();
  const [pendingRequests, setPendingRequests] = useState([]);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [confirmPaymentRequests, setConfirmPaymentRequests] = useState([]);
  const [approvedTransactions, setApprovedTransactions] = useState([]); // NEW: For finance transactions
  const [loading, setLoading] = useState(true);
  const [budgets, setBudgets] = useState([]);
  const [processingId, setProcessingId] = useState(null);
  const [activeTab, setActiveTab] = useState("approval"); // 'approval', 'payment', or 'confirm_payment'
  const [viewMode, setViewMode] = useState("kanban"); // 'kanban', 'list', 'dashboard'
  const [collapsedColumns, setCollapsedColumns] = useState({}); // Track collapsed state for each priority
  const [showFinanceModal, setShowFinanceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showConfirmPaymentModal, setShowConfirmPaymentModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedTransaction, setSelectedTransaction] = useState(null); // NEW: For finance transactions
  const [financeData, setFinanceData] = useState({
    action: "",
    comments: "",
    budget_code: "",
    budget_amount: "",
    payment_method: "bank_transfer",
    finance_notes: "",
  });
  // Get default payment letter template
  const getDefaultTemplate = (request) => {
    const today = new Date();
    const day = today.getDate().toString().padStart(2, "0");
    const month = (today.getMonth() + 1).toString().padStart(2, "0");
    const year = today.getFullYear();
    const defaultDate = `${day}/${month}/${year}`;

    // Check if payment method is cash
    const isCashPayment = request?.payment_method === "cash";

    if (isCashPayment) {
      // Cash payment template - no beneficiary section
      return `${defaultDate}

To the Manager,
Access Bank.

Dear Sir,

DEBIT INSTRUCTION

Kindly take this as authorization to debit our account number 1876520128 - Puffin Agro Allied & General Services Limited with a total amount of [AMOUNT WILL BE AUTO-FILLED] and provide cash of the same amount.

Thank you

Yours faithfully,
___SIGNATURE_MARKER___

KABIR AL-AMIN ABBA
Group Managing Director`;
    } else {
      // Bank transfer template - with beneficiary details
      return `${defaultDate}

To the Manager,
Access Bank.

Dear Sir,

DEBIT INSTRUCTION

Kindly take this as authorization to debit our account number 1876520128 - Puffin Agro Allied & General Services Limited with a total amount of [AMOUNT WILL BE AUTO-FILLED] and transfer to the following beneficiary.

Beneficiary:        ${request?.vendor_name || "[Vendor Name]"}
Bank Details:       [BANK DETAILS WILL BE AUTO-FILLED]
Amount:             [AMOUNT WILL BE AUTO-FILLED]

Thank you

Yours faithfully,
___SIGNATURE_MARKER___

KABIR AL-AMIN ABBA
Group Managing Director`;
    }
  };

  const [paymentData, setPaymentData] = useState({
    payment_reference: "",
    transaction_id: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "bank_transfer",
    comments: "",
    letterhead: "Template.pdf",
    document_template: "",
  });

  const [confirmPaymentData, setConfirmPaymentData] = useState({
    payment_reference: "",
    transaction_id: "",
    payment_date: new Date().toISOString().split("T")[0],
    payment_method: "bank_transfer",
    comments: "",
  });

  const [originalTemplate, setOriginalTemplate] = useState("");

  // Validation function to check if protected placeholders are intact
  const validateTemplate = () => {
    const template = paymentData.document_template;
    const originalTemp = originalTemplate;

    console.log("🔍 validateTemplate called:", {
      templateLength: template?.length || 0,
      originalLength: originalTemp?.length || 0,
      hasTemplate: !!template,
      hasOriginal: !!originalTemp,
    });

    // If templates haven't been loaded yet, consider it valid (modal is initializing)
    if (!template || !originalTemp) {
      console.log("⚠️ Templates not loaded yet, returning valid");
      return { valid: true, message: "" };
    }

    // Count occurrences of AUTO-FILLED in both templates
    const countAutoFilled = (text) => {
      const matches = text.match(/AUTO-FILLED/g);
      return matches ? matches.length : 0;
    };

    const originalCount = countAutoFilled(originalTemp);
    const currentCount = countAutoFilled(template);

    console.log("🔍 AUTO-FILLED counts:", {
      original: originalCount,
      current: currentCount,
    });

    // If any AUTO-FILLED text has been removed
    if (currentCount < originalCount) {
      console.log("❌ AUTO-FILLED text removed!");
      return {
        valid: false,
        message: `Protected "AUTO-FILLED" placeholder has been removed or modified. Please restore the original template.`,
      };
    }

    // Check for required placeholders based on payment method
    const requiredPlaceholders = ["[AMOUNT WILL BE AUTO-FILLED]"];

    // Only require bank details for transfer payments
    if (selectedRequest?.payment_method !== "cash") {
      requiredPlaceholders.push("[BANK DETAILS WILL BE AUTO-FILLED]");
    }

    console.log("🔍 Checking required placeholders:", requiredPlaceholders);

    for (const placeholder of requiredPlaceholders) {
      if (!template.includes(placeholder)) {
        console.log(`❌ Missing placeholder: ${placeholder}`);
        return {
          valid: false,
          message: `Protected placeholder "${placeholder}" has been removed or modified. Please restore it.`,
        };
      }
    }

    console.log("✅ Validation passed!");
    return { valid: true, message: "" };
  };

  // Available letterheads
  const letterheads = [
    { value: "Template.pdf", label: "Default Template" },
    { value: "Buildwise.pdf", label: "Buildwise" },
    { value: "lasershield.pdf", label: "Lasershield" },
    { value: "Maz logistics.pdf", label: "Maz Logistics" },
    { value: "Puffin agro.pdf", label: "Puffin Agro" },
    { value: "Puffin energy.pdf", label: "Puffin Energy" },
    { value: "Swingtide.pdf", label: "Swingtide" },
  ];

  useEffect(() => {
    fetchFinanceRequests();
    fetchBudgets();
  }, []);

  const fetchBudgets = async () => {
    try {
      const response = await financeService.getBudgets({ limit: 1000 });
      const budgetData =
        response.data?.data?.budgets || response.data?.data || [];
      console.log("Budget data from API:", budgetData);
      if (budgetData.length > 0) {
        console.log("First budget structure:", budgetData[0]);
      }
      setBudgets(Array.isArray(budgetData) ? budgetData : []);
    } catch (error) {
      console.error("Failed to fetch budgets:", error);
      setBudgets([]);
    }
  };

  const handleDownloadPaymentLetter = async () => {
    if (!selectedRequest) {
      throw new Error("No request selected");
    }

    // Validate template before downloading
    const validation = validateTemplate();
    if (!validation.valid) {
      throw new Error(validation.message);
    }

    try {
      const pdfBytes = await generatePaymentLetterPDF();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Payment_Letter_${selectedRequest.request_id || selectedRequest.id}_${new Date().toISOString().split("T")[0]}.pdf`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();

      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);

      console.log("Payment letter downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw new Error("Failed to generate PDF: " + error.message);
    }
  };

  const handlePreviewPaymentLetter = async () => {
    if (!selectedRequest) {
      toast.error("No request selected");
      return;
    }

    const validation = validateTemplate();
    if (!validation.valid) {
      toast.error(validation.message);
      return;
    }

    try {
      const pdfBytes = await generatePaymentLetterPDF();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setShowPreviewModal(true);
    } catch (error) {
      console.error("Error generating preview:", error);
      toast.error("Failed to generate preview: " + error.message);
    }
  };

  const generatePaymentLetterPDF = async () => {
    if (!selectedRequest) {
      throw new Error("No request selected");
    }

    // Validate template before downloading
    const validation = validateTemplate();
    if (!validation.valid) {
      throw new Error(validation.message);
    }

    try {
      console.log("Starting PDF generation...");

      // Create a new PDF document without letterhead
      const pdfDoc = await PDFDocument.create();

      // Embed fonts
      const helveticaBold = await pdfDoc.embedFont("Helvetica-Bold");

      // Add a blank page
      const page = pdfDoc.addPage([595.28, 841.89]); // A4 size
      const { width, height } = page.getSize();
      const fontSize = 11;
      const margin = 30; // Reduced left margin for letterhead
      let yPosition = height - 180; // Increased top margin for letterhead logo space

      // Replace placeholders in the template with actual values
      let finalContent = paymentData.document_template;

      // Replace amount placeholders (use 'N' instead of '₦' for PDF compatibility)
      const amountValue = selectedRequest.amount
        ? parseFloat(selectedRequest.amount).toLocaleString("en-NG", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })
        : "0.00";
      const amountText = `N${amountValue}`; // Use 'N' instead of '₦'
      finalContent = finalContent.replace(
        /\[AMOUNT WILL BE AUTO-FILLED\]/g,
        amountText,
      );

      // Replace bank details placeholders
      const bankDetails = selectedRequest.vendor_bank_details || "Not provided";

      // Format bank details with proper line breaks and indentation
      // If bank details contain multiple lines, format them properly
      const formattedBankDetails = bankDetails
        .split("\n")
        .map((line, index) => {
          // First line stays on the same line as "Bank Details:"
          if (index === 0) return line.trim();
          // Subsequent lines get proper indentation to align with the first line
          return `                    ${line.trim()}`;
        })
        .join("\n");

      finalContent = finalContent.replace(
        /\[BANK DETAILS WILL BE AUTO-FILLED\]/g,
        formattedBankDetails,
      );

      // Replace vendor name
      finalContent = finalContent.replace(
        /\[Vendor Name\]/g,
        selectedRequest.vendor_name || "N/A",
      );

      // Remove any remaining ₦ symbols that might cause encoding issues
      finalContent = finalContent.replace(/₦/g, "N");

      // Use local GMD signature from public folder
      const gmdSignatureUrl = "/signature.png";

      // The template already has ___SIGNATURE_MARKER___ in it
      console.log("Looking for ___SIGNATURE_MARKER___ in content...");
      console.log(
        "Content includes marker:",
        finalContent.includes("___SIGNATURE_MARKER___"),
      );

      // Function to wrap text to fit within page width
      const wrapText = (text, maxWidth, preserveIndent = false) => {
        const words = text.split(" ");
        const lines = [];
        let currentLine = "";

        // Detect if line starts with spaces (indentation)
        const indent = (preserveIndent && text.match(/^(\s+)/)?.[1]) || "";
        const indentWidth = indent.length * (fontSize * 0.3);

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;
          const testWidth = testLine.length * (fontSize * 0.5); // Approximate width

          if (testWidth > maxWidth - indentWidth && currentLine) {
            lines.push(currentLine);
            currentLine = indent + word; // Add indent to wrapped lines
          } else {
            currentLine = testLine;
          }
        }

        if (currentLine) {
          lines.push(currentLine);
        }

        return lines;
      };

      // Split content into lines and wrap long lines
      const rawLines = finalContent.split("\n");
      const lines = [];
      const maxWidth = width - margin * 2;

      for (const rawLine of rawLines) {
        if (rawLine.trim() === "") {
          lines.push(""); // Preserve empty lines
        } else {
          // Check if line has indentation (for bank details continuation)
          const hasIndent = rawLine.startsWith("                    ");
          const wrappedLines = wrapText(rawLine, maxWidth, hasIndent);
          lines.push(...wrappedLines);
        }
      }

      // Draw text on the page
      let currentPage = page;
      let signatureYPosition = null;
      let isFirstLine = true;

      console.log("=== DRAWING TEXT ===");
      console.log("Total lines to draw:", lines.length);

      for (const line of lines) {
        if (yPosition < 50) {
          // Add new blank page if content is too long
          currentPage = pdfDoc.addPage([595.28, 841.89]);
          yPosition = height - 180; // Leave space for letterhead on new pages too
        }

        // Check if this line contains the signature marker
        if (line.includes("___SIGNATURE_MARKER___")) {
          signatureYPosition = yPosition;
          console.log("✓ Found signature marker at Y position:", yPosition);
          // Don't draw the marker text, just note the position
          yPosition -= 60; // Leave space for signature image
          continue;
        }

        // Calculate text width for alignment
        const isBoldLine = line.trim() === "DEBIT INSTRUCTION";
        const currentFont = isBoldLine ? helveticaBold : undefined;
        const textWidth = line.length * (fontSize * 0.5);
        let xPosition = margin;

        // Right-align the date (first line)
        if (isFirstLine && line.trim()) {
          xPosition = width - margin - textWidth;
          isFirstLine = false;
        }
        // Center-align "REQUEST FOR TRANSFER"
        else if (isBoldLine) {
          xPosition = (width - textWidth) / 2;
        }

        const drawOptions = {
          x: xPosition,
          y: yPosition,
          size: fontSize,
          color: rgb(0, 0, 0),
        };

        if (currentFont) {
          drawOptions.font = currentFont;
        }

        currentPage.drawText(line, drawOptions);

        yPosition -= fontSize + 4;
      }

      // Add GMD Signature image if available
      console.log("=== SIGNATURE EMBEDDING ===");
      console.log("Has signature URL:", !!gmdSignatureUrl);
      console.log("Has signature position:", !!signatureYPosition);

      if (gmdSignatureUrl && signatureYPosition) {
        try {
          // Remove query parameters that might convert the image format
          const cleanUrl = gmdSignatureUrl.split("?")[0];
          console.log(
            "Adding GMD signature to payment letter at position:",
            signatureYPosition,
          );
          console.log("Original URL:", gmdSignatureUrl);
          console.log("Clean URL:", cleanUrl);

          // Fetch the signature image
          const signatureResponse = await fetch(cleanUrl);
          console.log(
            "Signature fetch response status:",
            signatureResponse.status,
          );
          if (signatureResponse.ok) {
            const signatureBytes = await signatureResponse.arrayBuffer();

            // Embed the signature image in the PDF
            let signatureImage;
            try {
              // Try PNG first (most common for signatures)
              signatureImage = await pdfDoc.embedPng(signatureBytes);
              console.log("Embedded as PNG");
            } catch (pngError) {
              try {
                // If PNG fails, try JPG
                signatureImage = await pdfDoc.embedJpg(signatureBytes);
                console.log("Embedded as JPG");
              } catch (jpgError) {
                console.error(
                  "Failed to embed signature as PNG or JPG:",
                  pngError,
                  jpgError,
                );
                throw new Error("Unsupported image format");
              }
            }

            // Get signature dimensions and make it square with fixed size
            const maxSize = 80; // Fixed square size
            const { width: imgWidth, height: imgHeight } = signatureImage;
            const scale = Math.min(maxSize / imgWidth, maxSize / imgHeight);
            const finalWidth = imgWidth * scale;
            const finalHeight = imgHeight * scale;

            // Draw signature on the page
            currentPage.drawImage(signatureImage, {
              x: margin,
              y: signatureYPosition - finalHeight - 10,
              width: finalWidth,
              height: finalHeight,
            });

            console.log("✓ GMD signature added successfully at:", {
              x: margin,
              y: signatureYPosition - finalHeight - 10,
              width: finalWidth,
              height: finalHeight,
            });
          }
        } catch (error) {
          console.error("Error adding GMD signature:", error);
        }
      }

      // Save the PDF
      const pdfBytes = await pdfDoc.save();
      return pdfBytes;
    } catch (error) {
      console.error("Error generating PDF:", error);
      throw new Error("Failed to generate PDF: " + error.message);
    }
  };

  const fetchFinanceRequests = async () => {
    try {
      setLoading(true);

      // Fetch ALL requests pending finance approval (no user filtering)
      const approvalResponse = await purchaseRequestService.getAllRequests({
        status: "pending_finance_approval",
        current_stage: "finance_stage",
        show_all: true, // IMPORTANT: Remove user restrictions - show ALL finance requests
        limit: 100, // Increased limit to fetch more requests
      });
      setPendingRequests(approvalResponse.data.data.requests || []);

      // Fetch ALL requests in payment processing (no user filtering)
      const paymentResponse = await purchaseRequestService.getAllRequests({
        status: "payment_in_progress",
        current_stage: "pay_vendor_stage",
        show_all: true, // IMPORTANT: Remove user restrictions - show ALL payment requests
        limit: 100, // Increased limit to fetch more requests
      });

      // NEW: Fetch approved transactions from finance module
      try {
        const transactionsResponse =
          await financeService.getApprovedTransactions();
        const transactions = transactionsResponse.data.data?.transactions || [];

        // Convert transactions to match purchase request format for display
        const formattedTransactions = transactions.map((t) => ({
          id: `trans-${t.id}`,
          request_id: t.transaction_number || `TRANS-${t.id}`,
          requester_name: "Finance Department",
          vendor_name: t.beneficiary_name || "N/A",
          vendor_bank_details:
            t.payment_method === "transfer"
              ? `${t.beneficiary_bank || ""}\nAccount: ${t.beneficiary_account || ""}`.trim()
              : "Cash Payment",
          amount: t.amount,
          item_service_requested: t.description,
          priority: "medium",
          payment_method:
            t.payment_method === "cash" ? "cash" : "bank_transfer",
          isTransaction: true, // Flag to identify transactions
          originalTransaction: t, // Keep original data
        }));

        // Merge with purchase requests
        setPaymentRequests([
          ...(paymentResponse.data.data.requests || []),
          ...formattedTransactions,
        ]);
      } catch (transError) {
        console.error("Error fetching transactions:", transError);
        // If transaction fetch fails, just show purchase requests
        setPaymentRequests(paymentResponse.data.data.requests || []);
      }

      // Fetch ALL requests awaiting payment confirmation (no user filtering)
      const confirmPaymentResponse =
        await purchaseRequestService.getAllRequests({
          status: "awaiting_payment_confirmation",
          current_stage: "confirm_payment_stage",
          show_all: true,
          limit: 100,
        });

      setConfirmPaymentRequests(
        confirmPaymentResponse.data.data.requests || [],
      );
    } catch (error) {
      console.error("Fetch finance requests error:", error);
      toast.error("Failed to fetch finance requests");
    } finally {
      setLoading(false);
    }
  };

  // Auto-refresh when a finance/procurement notification arrives via WebSocket
  useNotificationRefresh(["purchase_request_approval"], fetchFinanceRequests);

  const handleFinanceApproval = async (
    requestId,
    action,
    additionalData = {},
  ) => {
    try {
      setProcessingId(requestId);

      // Budget validation when approving
      if (action === "approve" && additionalData.budget_code) {
        const selectedBudget = budgets.find(
          (b) => b.code === additionalData.budget_code,
        );

        if (selectedBudget) {
          const requestAmount = parseFloat(selectedRequest?.amount || 0);
          const budgetBalance = parseFloat(
            selectedBudget.available_amount || 0,
          );

          if (requestAmount > budgetBalance) {
            toast.error(
              `Insufficient budget! Request: ₦${requestAmount.toLocaleString()}, Available: ₦${budgetBalance.toLocaleString()}`,
            );
            setProcessingId(null);
            return;
          }
        }
      }

      const data = {
        action: action,
        comments: additionalData.comments || "",
        stage: "finance_stage",
        additional_data: {
          budget_code: additionalData.budget_code || "",
          payment_method: additionalData.payment_method || "bank_transfer",
          finance_notes: additionalData.finance_notes || "",
          budget_approval_reference:
            additionalData.budget_approval_reference || "",
          request_amount: selectedRequest?.amount || 0,
        },
      };

      await purchaseRequestService.processFinance(requestId, data);

      toast.success(
        `Request ${action === "approve" ? "approved for payment" : "rejected"} successfully`,
      );

      // Refresh the lists and budgets
      fetchFinanceRequests();
      fetchBudgets();

      // Close modals
      setShowFinanceModal(false);
      setShowViewModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error("Finance approval error:", error);
      toast.error(
        error.response?.data?.message || `Failed to ${action} request`,
      );
    } finally {
      setProcessingId(null);
    }
  };

  const handlePaymentProcessing = async (requestId, paymentData) => {
    try {
      setProcessingId(requestId);

      const data = {
        action: "complete",
        comments: paymentData.comments || "",
        stage: "pay_vendor_stage",
        additional_data: {
          payment_reference: paymentData.payment_reference,
          transaction_id: paymentData.transaction_id || "",
          payment_date:
            paymentData.payment_date || new Date().toISOString().split("T")[0],
          payment_method: paymentData.payment_method || "bank_transfer",
        },
      };

      await purchaseRequestService.processPayment(requestId, data);

      toast.success("Payment processed successfully");

      // Refresh the lists
      fetchFinanceRequests();

      // Close modals
      setShowPaymentModal(false);
      setShowViewModal(false);
      setSelectedRequest(null);
    } catch (error) {
      console.error("Payment processing error:", error);
      toast.error(error.response?.data?.message || "Failed to process payment");
    } finally {
      setProcessingId(null);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-NG", {
      style: "currency",
      currency: "NGN",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const handleDownloadReceipt = (request) => {
    setSelectedRequest(request);
    setShowReceipt(true);
  };

  const groupByPriority = (requests) => {
    const groups = {
      urgent: [],
      high: [],
      medium: [],
      low: [],
    };

    requests.forEach((request) => {
      const priority = request.priority || "medium";
      if (groups[priority]) {
        groups[priority].push(request);
      }
    });

    return groups;
  };

  const getCurrentRequests = () => {
    if (activeTab === "approval") return pendingRequests;
    if (activeTab === "payment") return paymentRequests;
    if (activeTab === "confirm_payment") return confirmPaymentRequests;
    return [];
  };

  const toggleColumnCollapse = (priority) => {
    setCollapsedColumns((prev) => ({
      ...prev,
      [priority]: !prev[priority],
    }));
  };

  const openFinanceModal = (request, action) => {
    setSelectedRequest(request);
    setFinanceData({
      action: action,
      comments: "",
      budget_code: "",
      budget_amount: "",
      payment_method: "bank_transfer",
      finance_notes: "",
    });
    setShowFinanceModal(true);
  };

  const openPaymentModal = (request) => {
    console.log("📂 openPaymentModal called with request:", request);

    setSelectedRequest(request);

    // Check if this is a transaction or purchase request
    if (request.isTransaction) {
      setSelectedTransaction(request.originalTransaction);
    } else {
      setSelectedTransaction(null);
    }

    const template = getDefaultTemplate(request);
    console.log("📝 Generated template length:", template.length);
    console.log("📝 Template preview:", template.substring(0, 200) + "...");

    setOriginalTemplate(template); // Store original template
    setPaymentData({
      payment_reference: "",
      transaction_id: "",
      payment_date: new Date().toISOString().split("T")[0],
      payment_method: request.payment_method || "bank_transfer",
      comments: "",
      letterhead: "Template.pdf",
      document_template: template,
    });

    console.log("✅ Payment modal state set, opening modal...");
    setShowPaymentModal(true);
  };

  const handleSubmitFinanceAction = () => {
    if (!financeData.comments.trim()) {
      toast.warning("Please provide comments for this action");
      return;
    }

    if (financeData.action === "approve" && !financeData.budget_code.trim()) {
      toast.warning("Please provide a budget code for approval");
      return;
    }

    const additionalData = {
      budget_code: financeData.budget_code,
      payment_method: financeData.payment_method,
      comments: financeData.comments,
      finance_notes:
        financeData.finance_notes ||
        (financeData.action === "approve"
          ? "Budget approved and payment authorized"
          : "Request rejected during finance review"),
    };

    handleFinanceApproval(
      selectedRequest.request_id,
      financeData.action,
      additionalData,
    );
    setShowFinanceModal(false);
    setSelectedRequest(null);
  };

  const handleSubmitPayment = async () => {
    console.log("🔵 ===== BUTTON CLICKED: handleSubmitPayment CALLED =====");
    console.log("🔵 selectedRequest exists:", !!selectedRequest);

    if (!selectedRequest) {
      console.log("❌ ERROR: No request selected");
      toast.error("No request selected");
      return;
    }

    console.log("🔵 Running validation...");
    const validation = validateTemplate();
    console.log("🔵 Validation result:", validation);

    if (!validation.valid) {
      console.log("❌ ERROR: Validation failed -", validation.message);
      toast.error(validation.message);
      return;
    }

    console.log("✅ Validation passed! Proceeding with payment processing...");

    try {
      setProcessingId(selectedRequest.request_id || selectedRequest.id);

      console.log("=== FRONTEND PAYMENT PROCESSING ===");
      console.log("Selected Request:", selectedRequest);
      console.log("Is Transaction:", selectedRequest.isTransaction);
      console.log("Request ID:", selectedRequest.request_id);

      if (selectedRequest.isTransaction) {
        // For transactions: just download the letter, don't change status
        console.log("Processing transaction - download only, no status update");
        await handleDownloadPaymentLetter();
        toast.success("Payment letter downloaded successfully!");
      } else {
        console.log("Processing purchase request - will update status");
        console.log("Calling processPayment API with:", {
          requestId: selectedRequest.request_id,
          letterhead: paymentData.letterhead,
          status: "awaiting_payment_confirmation",
          current_stage: "confirm_payment_stage",
        });

        const response = await purchaseRequestService.processPayment(
          selectedRequest.request_id,
          {
            letterhead: paymentData.letterhead,
            document_template: paymentData.document_template,
            status: "awaiting_payment_confirmation",
            current_stage: "confirm_payment_stage",
          },
        );

        console.log("API Response:", response);

        // Optimistically move the request from payment to confirm payment in local state
        setPaymentRequests((prev) =>
          prev.filter((r) => r.request_id !== selectedRequest.request_id),
        );
        setConfirmPaymentRequests((prev) => [
          {
            ...selectedRequest,
            current_stage: "confirm_payment_stage",
            status: "awaiting_payment_confirmation",
          },
          ...prev,
        ]);

        // Switch to Confirm Payment tab
        console.log("Switching to confirm_payment tab");
        setActiveTab("confirm_payment");

        toast.success(
          "Payment processed successfully! Request moved to Confirm Payment stage.",
        );

        // Try to download the PDF letter (non-blocking - don't let it prevent the tab switch)
        try {
          await handleDownloadPaymentLetter();
          toast.success("Payment letter downloaded successfully!");
        } catch (pdfError) {
          console.error("PDF download failed, but payment was already processed:", pdfError);
          toast.error(
            "Payment was processed but the letter could not be downloaded. Please try downloading from the request details.",
          );
        }
      }

      setShowPaymentModal(false);
      setSelectedRequest(null);
      setSelectedTransaction(null);
      setPaymentData({
        payment_reference: "",
        transaction_id: "",
        payment_date: new Date().toISOString().split("T")[0],
        payment_method: "bank_transfer",
        comments: "",
        letterhead: "Template.pdf",
        document_template: "",
      });

      // Refresh from server (non-blocking - local state already updated)
      fetchFinanceRequests();
    } catch (error) {
      console.error("Error processing payment:", error);
      const errorMessage =
        error.message ||
        error.response?.data?.message ||
        "Failed to process payment";
      toast.error(errorMessage);
    } finally {
      setProcessingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Finance & Payment Processing
          </h1>
          <p className="text-gray-600 mt-1">
            Approve budgets and process vendor payments
          </p>
        </div>
        <div className="flex items-center space-x-4">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode("kanban")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === "kanban"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <LayoutGrid size={18} />
              <span className="text-sm font-medium">Kanban</span>
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === "list"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <ListIcon size={18} />
              <span className="text-sm font-medium">List</span>
            </button>
            <button
              onClick={() => setViewMode("dashboard")}
              className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-colors ${
                viewMode === "dashboard"
                  ? "bg-white text-blue-600 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              <BarChart3 size={18} />
              <span className="text-sm font-medium">Dashboard</span>
            </button>
          </div>

          <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm font-medium">
            {pendingRequests.length} Pending Approval
          </span>
          <span className="bg-pink-100 text-pink-800 px-3 py-1 rounded-full text-sm font-medium">
            {paymentRequests.length} Payment Processing
          </span>

          <button
            onClick={fetchFinanceRequests}
            disabled={loading}
            className="flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Refresh Finance Requests"
          >
            <RefreshCw
              className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`}
            />
            <span className="text-sm font-medium">Refresh</span>
          </button>
        </div>
      </div>

      {/* Kanban View */}
      {viewMode === "kanban" && (
        <div className="space-y-6">
          {/* Tabs for Kanban */}
          <div className="border-b border-gray-200">
            <div className="flex items-center justify-between">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("approval")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "approval"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Budget Approval ({pendingRequests.length})
                </button>
                <button
                  onClick={() => setActiveTab("payment")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "payment"
                      ? "border-pink-500 text-pink-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Pay Vendor ({paymentRequests.length})
                </button>
                <button
                  onClick={() => setActiveTab("confirm_payment")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "confirm_payment"
                      ? "border-green-500 text-green-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Confirm Payment ({confirmPaymentRequests.length})
                </button>
              </nav>
            </div>
          </div>

          {/* Kanban Board */}
          <div className="flex gap-4">
            {Object.entries(groupByPriority(getCurrentRequests())).map(
              ([priority, requests]) => {
                const config = {
                  urgent: {
                    label: "Urgent",
                    color: "red",
                    bgColor: "bg-red-50",
                    borderColor: "border-red-200",
                  },
                  high: {
                    label: "High",
                    color: "orange",
                    bgColor: "bg-orange-50",
                    borderColor: "border-orange-200",
                  },
                  medium: {
                    label: "Medium",
                    color: "yellow",
                    bgColor: "bg-yellow-50",
                    borderColor: "border-yellow-200",
                  },
                  low: {
                    label: "Low",
                    color: "green",
                    bgColor: "bg-green-50",
                    borderColor: "border-green-200",
                  },
                }[priority];

                const isCollapsed = collapsedColumns[priority];

                return (
                  <div
                    key={priority}
                    className={`flex flex-col transition-all duration-300 ease-in-out ${
                      isCollapsed ? "w-16" : "w-80"
                    }`}
                  >
                    {isCollapsed ? (
                      // Collapsed view - vertical text
                      <div
                        className={`${config.bgColor} ${config.borderColor} border-2 rounded-lg p-3 cursor-pointer hover:opacity-90 transition-opacity h-full flex flex-col items-center justify-center`}
                        onClick={() => toggleColumnCollapse(priority)}
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <ChevronDown className="h-4 w-4 text-gray-600 rotate-90" />
                          <div
                            className="whitespace-nowrap font-semibold text-gray-900 text-sm"
                            style={{
                              writingMode: "vertical-rl",
                              textOrientation: "mixed",
                            }}
                          >
                            {config.label}
                          </div>
                          <span
                            className={`bg-${config.color}-100 text-${config.color}-800 px-2 py-1 rounded-full text-xs`}
                          >
                            {requests.length}
                          </span>
                        </div>
                      </div>
                    ) : (
                      // Expanded view
                      <>
                        <div
                          className={`${config.bgColor} ${config.borderColor} border-2 rounded-t-lg p-3 cursor-pointer hover:opacity-90 transition-opacity`}
                          onClick={() => toggleColumnCollapse(priority)}
                        >
                          <h3 className="font-semibold text-gray-900 flex items-center justify-between">
                            <span className="flex items-center space-x-2">
                              <span>{config.label} Priority</span>
                              <ChevronUp className="h-4 w-4 text-gray-600 -rotate-90" />
                            </span>
                            <span
                              className={`bg-${config.color}-100 text-${config.color}-800 px-2 py-1 rounded-full text-xs`}
                            >
                              {requests.length}
                            </span>
                          </h3>
                        </div>
                        <div className="bg-gray-50 border-l-2 border-r-2 border-b-2 border-gray-200 rounded-b-lg overflow-hidden">
                          <div className="p-2 space-y-2 min-h-[400px] max-h-[600px] overflow-y-auto">
                            {requests.map((request) => (
                              <div
                                key={request.id}
                                className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow"
                              >
                                <div className="text-sm font-semibold text-blue-600 mb-2">
                                  {request.request_id}
                                </div>
                                <p className="text-xs text-gray-600 mb-1">
                                  <strong>Requester:</strong>{" "}
                                  {request.requester_name}
                                </p>
                                <p className="text-xs text-gray-600 mb-1">
                                  <strong>Vendor:</strong> {request.vendor_name}
                                </p>
                                <p
                                  className={`text-sm font-bold mb-2 ${activeTab === "approval" ? "text-indigo-900" : "text-pink-900"}`}
                                >
                                  {formatCurrency(request.amount)}
                                </p>
                                <p className="text-xs text-gray-700 mb-3 line-clamp-2">
                                  {request.item_service_requested}
                                </p>

                                <button
                                  onClick={() => {
                                    setSelectedRequest(request);
                                    setShowViewModal(true);
                                  }}
                                  className="w-full mb-2 px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center justify-center space-x-2 text-sm"
                                >
                                  <Eye className="h-4 w-4" />
                                  <span>View Details</span>
                                </button>

                                {activeTab === "approval" ? (
                                  <div className="pt-2 border-t border-gray-200 space-y-2">
                                    <button
                                      onClick={() =>
                                        openFinanceModal(request, "approve")
                                      }
                                      disabled={
                                        processingId === request.request_id
                                      }
                                      className="w-full px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                    >
                                      Approve Budget
                                    </button>
                                    <button
                                      onClick={() =>
                                        openFinanceModal(request, "reject")
                                      }
                                      disabled={
                                        processingId === request.request_id
                                      }
                                      className="w-full px-3 py-1.5 border border-red-300 text-red-700 rounded text-xs hover:bg-red-50 disabled:opacity-50"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                ) : activeTab === "payment" ? (
                                  <div className="pt-2 border-t border-gray-200">
                                    <button
                                      onClick={() => openPaymentModal(request)}
                                      disabled={
                                        processingId === request.request_id
                                      }
                                      className="w-full px-3 py-1.5 bg-pink-600 text-white rounded text-xs hover:bg-pink-700 disabled:opacity-50"
                                    >
                                      Process Payment
                                    </button>
                                  </div>
                                ) : (
                                  <div className="pt-2 border-t border-gray-200">
                                    <button
                                      onClick={() => {
                                        setSelectedRequest(request);
                                        setShowConfirmPaymentModal(true);
                                      }}
                                      disabled={
                                        processingId === request.request_id
                                      }
                                      className="w-full px-3 py-1.5 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:opacity-50"
                                    >
                                      Confirm Payment
                                    </button>
                                  </div>
                                )}
                              </div>
                            ))}
                            {requests.length === 0 && (
                              <div className="text-center py-8 text-gray-400 text-sm">
                                No {config.label.toLowerCase()} priority
                                requests
                              </div>
                            )}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );
              },
            )}
          </div>
        </div>
      )}

      {/* Dashboard View */}
      {viewMode === "dashboard" && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-indigo-500">
              <p className="text-sm text-gray-600 mb-1">Pending Approval</p>
              <p className="text-3xl font-bold text-indigo-900">
                {pendingRequests.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrency(
                  pendingRequests.reduce(
                    (sum, req) => sum + parseFloat(req.amount),
                    0,
                  ),
                )}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-pink-500">
              <p className="text-sm text-gray-600 mb-1">Payment Processing</p>
              <p className="text-3xl font-bold text-pink-900">
                {paymentRequests.length}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {formatCurrency(
                  paymentRequests.reduce(
                    (sum, req) => sum + parseFloat(req.amount),
                    0,
                  ),
                )}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
              <p className="text-sm text-gray-600 mb-1">Total Value</p>
              <p className="text-2xl font-bold text-green-900">
                {formatCurrency(
                  [...pendingRequests, ...paymentRequests].reduce(
                    (sum, req) => sum + parseFloat(req.amount),
                    0,
                  ),
                )}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-500">
              <p className="text-sm text-gray-600 mb-1">Total Active</p>
              <p className="text-3xl font-bold text-gray-900">
                {pendingRequests.length + paymentRequests.length}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Recent Requests
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Request ID
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Requester
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Vendor
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Amount
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Priority
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {[...pendingRequests, ...paymentRequests]
                    .slice(0, 10)
                    .map((request) => (
                      <tr key={request.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <Link
                            to={`/purchase-requests/${request.request_id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            {request.request_id}
                          </Link>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {request.requester_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {request.vendor_name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {formatCurrency(request.amount)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${request.status === "pending_finance_approval" ? "bg-indigo-100 text-indigo-800" : "bg-pink-100 text-pink-800"}`}
                          >
                            {request.status === "pending_finance_approval"
                              ? "Pending Approval"
                              : "Payment Processing"}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              request.priority === "urgent"
                                ? "bg-red-100 text-red-800"
                                : request.priority === "high"
                                  ? "bg-orange-100 text-orange-800"
                                  : "bg-yellow-100 text-yellow-800"
                            }`}
                          >
                            {request.priority.toUpperCase()}
                          </span>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* List View - Tabs */}
      {viewMode === "list" && (
        <div>
          <div className="mb-6">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setActiveTab("approval")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "approval"
                      ? "border-indigo-500 text-indigo-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Budget Approval ({pendingRequests.length})
                </button>
                <button
                  onClick={() => setActiveTab("payment")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "payment"
                      ? "border-pink-500 text-pink-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Pay Vendor ({paymentRequests.length})
                </button>
                <button
                  onClick={() => setActiveTab("confirm_payment")}
                  className={`py-2 px-1 border-b-2 font-medium text-sm ${
                    activeTab === "confirm_payment"
                      ? "border-green-500 text-green-600"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                  }`}
                >
                  Confirm Payment ({confirmPaymentRequests.length})
                </button>
              </nav>
            </div>
          </div>

          {/* Budget Approval Tab */}
          {activeTab === "approval" && (
            <div className="space-y-6">
              {pendingRequests.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow-md">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">
                      No pending budget approvals
                    </p>
                    <p className="text-sm mt-1">
                      All requests have been processed
                    </p>
                  </div>
                </div>
              ) : (
                pendingRequests.map((request) => (
                  <div
                    key={request.id}
                    className="bg-white rounded-lg shadow-md border border-gray-200"
                  >
                    {/* Request Header */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-indigo-50">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Request {request.request_id}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Ready for budget approval and payment authorization
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-indigo-900">
                            {formatCurrency(request.amount)}
                          </p>
                          <p className="text-sm text-indigo-600 font-medium">
                            BUDGET APPROVAL REQUIRED
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Request Details */}
                    <div className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Request Information
                          </h4>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Requester:</span>{" "}
                              {request.requester_name}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Department:</span>{" "}
                              {purchaseRequestService.formatDepartment(
                                request.department,
                              )}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Vendor:</span>{" "}
                              {request.vendor_name}
                            </p>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Financial Details
                          </h4>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Amount:</span>{" "}
                              {formatCurrency(request.amount)}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Priority:</span>
                              <span
                                className={`ml-1 px-2 py-1 rounded-full text-xs font-medium ${
                                  request.priority === "urgent"
                                    ? "bg-red-100 text-red-800"
                                    : request.priority === "high"
                                      ? "bg-orange-100 text-orange-800"
                                      : "bg-yellow-100 text-yellow-800"
                                }`}
                              >
                                {request.priority.toUpperCase()}
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Item/Service
                        </h4>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">
                          {request.item_service_requested}
                        </p>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Vendor Bank Details
                        </h4>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border whitespace-pre-wrap">
                          {request.vendor_bank_details}
                        </p>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-center">
                        <Link
                          to={`/purchase-requests/${request.request_id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Full Details
                        </Link>

                        <div className="flex space-x-3">
                          <button
                            onClick={() => openFinanceModal(request, "reject")}
                            disabled={processingId === request.request_id}
                            className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
                          >
                            Reject Budget
                          </button>

                          <button
                            onClick={() => openFinanceModal(request, "approve")}
                            disabled={processingId === request.request_id}
                            className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
                          >
                            {processingId === request.request_id
                              ? "Processing..."
                              : "Approve & Authorize Payment"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Payment Processing Tab */}
          {activeTab === "payment" && (
            <div className="space-y-6">
              {paymentRequests.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow-md">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">
                      No payments to process
                    </p>
                    <p className="text-sm mt-1">
                      All authorized payments have been completed
                    </p>
                  </div>
                </div>
              ) : (
                paymentRequests.map((request) => (
                  <div
                    key={request.id}
                    className="bg-white rounded-lg shadow-md border border-gray-200"
                  >
                    {/* Request Header */}
                    <div className="px-6 py-4 border-b border-gray-200 bg-pink-50">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Payment {request.request_id}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Budget approved - Ready for vendor payment
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-pink-900">
                            {formatCurrency(request.amount)}
                          </p>
                          <p className="text-sm text-pink-600 font-medium">
                            PAYMENT PROCESSING
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Payment Details */}
                    <div className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Payment Information
                          </h4>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Vendor:</span>{" "}
                              {request.vendor_name}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Amount:</span>{" "}
                              {formatCurrency(request.amount)}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Requester:</span>{" "}
                              {request.requester_name}
                            </p>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Payment Instructions
                          </h4>
                          <div className="bg-green-50 border border-green-200 rounded p-3">
                            <p className="text-sm text-green-800">
                              ✅ Budget approved and payment authorized
                            </p>
                            <p className="text-xs text-green-700 mt-1">
                              Proceed with vendor payment using provided bank
                              details
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Vendor Bank Details
                        </h4>
                        <p className="text-sm text-gray-900 bg-yellow-50 p-3 rounded border border-yellow-200 whitespace-pre-wrap">
                          {request.vendor_bank_details}
                        </p>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Item/Service
                        </h4>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">
                          {request.item_service_requested}
                        </p>
                      </div>
                    </div>

                    {/* Payment Action */}
                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-center">
                        <Link
                          to={`/purchase-requests/${request.request_id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Full Details
                        </Link>

                        <button
                          onClick={() => openPaymentModal(request)}
                          disabled={processingId === request.request_id}
                          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          {processingId === request.request_id
                            ? "Processing..."
                            : "Confirm Payment Completed"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {/* Confirm Payment Tab */}
          {activeTab === "confirm_payment" && (
            <div className="space-y-6">
              {confirmPaymentRequests.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg shadow-md">
                  <div className="text-gray-500">
                    <p className="text-lg font-medium">
                      No payments to confirm
                    </p>
                    <p className="text-sm mt-1">
                      Awaiting payment processing completion
                    </p>
                  </div>
                </div>
              ) : (
                confirmPaymentRequests.map((request) => (
                  <div
                    key={request.id}
                    className="bg-white rounded-lg shadow-md border border-gray-200"
                  >
                    <div className="px-6 py-4 border-b border-gray-200 bg-green-50">
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            Payment {request.request_id}
                          </h3>
                          <p className="text-sm text-gray-600">
                            Payment sent - Awaiting confirmation
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-green-900">
                            {formatCurrency(request.amount)}
                          </p>
                          <p className="text-sm text-green-600 font-medium">
                            AWAITING CONFIRMATION
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="px-6 py-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Payment Information
                          </h4>
                          <div className="space-y-1">
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Vendor:</span>{" "}
                              {request.vendor_name}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Amount:</span>{" "}
                              {formatCurrency(request.amount)}
                            </p>
                            <p className="text-sm text-gray-900">
                              <span className="font-medium">Requester:</span>{" "}
                              {request.requester_name}
                            </p>
                          </div>
                        </div>

                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            Next Step
                          </h4>
                          <div className="bg-blue-50 border border-blue-200 rounded p-3">
                            <p className="text-sm text-blue-800">
                              ℹ️ Enter the payment reference to confirm payment
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">
                          Item/Service
                        </h4>
                        <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded border">
                          {request.item_service_requested}
                        </p>
                      </div>
                    </div>

                    <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
                      <div className="flex justify-between items-center">
                        <Link
                          to={`/purchase-requests/${request.request_id}`}
                          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                        >
                          View Full Details
                        </Link>

                        <button
                          onClick={() => {
                            setSelectedRequest(request);
                            setShowConfirmPaymentModal(true);
                          }}
                          disabled={processingId === request.request_id}
                          className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          {processingId === request.request_id
                            ? "Processing..."
                            : "Confirm Payment"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary */}
      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Finance Summary
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <p className="text-2xl font-bold text-indigo-900">
              {pendingRequests.length}
            </p>
            <p className="text-sm text-gray-600">Pending Approval</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-pink-900">
              {paymentRequests.length}
            </p>
            <p className="text-sm text-gray-600">Payment Processing</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-green-900">
              {formatCurrency(
                [...pendingRequests, ...paymentRequests].reduce(
                  (sum, req) => sum + parseFloat(req.amount),
                  0,
                ),
              )}
            </p>
            <p className="text-sm text-gray-600">Total Value</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {pendingRequests.length + paymentRequests.length}
            </p>
            <p className="text-sm text-gray-600">Total Active</p>
          </div>
        </div>
      </div>

      {/* View Modal */}
      {showViewModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                {activeTab === "approval"
                  ? "Finance Approval"
                  : "Payment Processing"}{" "}
                - {selectedRequest.request_id}
              </h3>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setSelectedRequest(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 max-h-[70vh] overflow-y-auto">
              <div
                className={`${activeTab === "approval" ? "bg-indigo-50" : "bg-pink-50"} p-4 rounded-lg`}
              >
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600">Amount</p>
                    <p
                      className={`text-2xl font-bold ${activeTab === "approval" ? "text-indigo-900" : "text-pink-900"}`}
                    >
                      {formatCurrency(selectedRequest.amount)}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Priority</p>
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                        selectedRequest.priority === "urgent"
                          ? "bg-red-100 text-red-800"
                          : selectedRequest.priority === "high"
                            ? "bg-orange-100 text-orange-800"
                            : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {selectedRequest.priority.toUpperCase()}
                    </span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Requester Information
                  </h4>
                  <p className="text-sm text-gray-700">
                    <strong>Name:</strong> {selectedRequest.requester_name}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Email:</strong> {selectedRequest.requester_email}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Department:</strong>{" "}
                    {purchaseRequestService.formatDepartment(
                      selectedRequest.department,
                    )}
                  </p>
                </div>

                <div className="bg-green-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Vendor Information
                  </h4>
                  <p className="text-sm text-gray-700">
                    <strong>Vendor:</strong> {selectedRequest.vendor_name}
                  </p>
                  <p className="text-sm text-gray-700">
                    <strong>Amount:</strong>{" "}
                    {formatCurrency(selectedRequest.amount)}
                  </p>
                </div>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Item/Service Requested
                </h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded border">
                  {selectedRequest.item_service_requested}
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Vendor Bank Details
                </h4>
                <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded border whitespace-pre-wrap">
                  {selectedRequest.vendor_bank_details}
                </p>
              </div>

              {/* Supporting Document */}
              {(selectedRequest.cdn_url ||
                selectedRequest.supporting_document_path) && (
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">
                    Supporting Document
                  </h4>
                  <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="h-6 w-6 text-blue-600" />
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Supporting Document
                        </p>
                        <p className="text-xs text-gray-500">
                          Click to view or download
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {(selectedRequest.cdn_url ||
                        selectedRequest.supporting_document_path) && (
                        <>
                          <a
                            href={
                              selectedRequest.cdn_url ||
                              selectedRequest.supporting_document_path
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            View
                          </a>
                          <a
                            href={
                              selectedRequest.cdn_url ||
                              selectedRequest.supporting_document_path
                            }
                            download
                            className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 transition-colors"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center pt-4 border-t border-gray-200 mt-6">
              <button
                onClick={() => handleDownloadReceipt(selectedRequest)}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                <Download className="h-4 w-4" />
                <span>Download Receipt</span>
              </button>

              {activeTab === "approval" ? (
                <div className="flex space-x-3">
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      openFinanceModal(selectedRequest, "reject");
                    }}
                    disabled={processingId === selectedRequest.request_id}
                    className="px-4 py-2 border border-red-300 text-red-700 rounded-md hover:bg-red-50 disabled:opacity-50"
                  >
                    Reject Budget
                  </button>
                  <button
                    onClick={() => {
                      setShowViewModal(false);
                      openFinanceModal(selectedRequest, "approve");
                    }}
                    disabled={processingId === selectedRequest.request_id}
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                  >
                    Approve Budget
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    openPaymentModal(selectedRequest);
                  }}
                  disabled={processingId === selectedRequest.request_id}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  Confirm Payment
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Finance Approval Modal */}
      {showFinanceModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {financeData.action === "approve"
                  ? "Approve Budget & Authorize Payment"
                  : "Reject Budget Request"}
              </h3>
              <button
                onClick={() => {
                  setShowFinanceModal(false);
                  setSelectedRequest(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 p-4 bg-gray-50 rounded-md">
              <h4 className="font-medium text-gray-900">Request Summary</h4>
              <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                <div>
                  <p>
                    <strong>ID:</strong> {selectedRequest.request_id}
                  </p>
                  <p>
                    <strong>Requester:</strong> {selectedRequest.requester_name}
                  </p>
                  <p>
                    <strong>Department:</strong>{" "}
                    {purchaseRequestService.formatDepartment(
                      selectedRequest.department,
                    )}
                  </p>
                </div>
                <div>
                  <p>
                    <strong>Amount:</strong>{" "}
                    {formatCurrency(selectedRequest.amount)}
                  </p>
                  <p>
                    <strong>Vendor:</strong> {selectedRequest.vendor_name}
                  </p>
                  <p>
                    <strong>Priority:</strong>{" "}
                    {selectedRequest.priority.toUpperCase()}
                  </p>
                </div>
              </div>
            </div>
            {/* {JSON.stringify(budgets)} */}
            <div className="space-y-4">
              {/* Budget Code (for approval) */}
              {financeData.action === "approve" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Budget *
                  </label>
                  <select
                    value={financeData.budget_code}
                    onChange={(e) => {
                      const selectedBudget = budgets.find(
                        (b) => (b.code || b.id) === e.target.value,
                      );
                      setFinanceData((prev) => ({
                        ...prev,
                        budget_code: e.target.value,
                        budget_amount: selectedBudget?.allocated_amount || "",
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">-- Select a Budget --</option>
                    {Array.isArray(budgets) &&
                      budgets.map((budget) => {
                        const balance = parseFloat(
                          budget.available_amount || 0,
                        );
                        const requestAmount = parseFloat(
                          selectedRequest?.amount || 0,
                        );
                        const insufficient = requestAmount > balance;

                        return (
                          <option
                            key={budget.id}
                            value={budget.code || budget.id}
                            disabled={insufficient}
                            style={{
                              color: insufficient ? "#ef4444" : "inherit",
                            }}
                          >
                            {budget.name} - Balance: ₦{balance.toLocaleString()}
                            {insufficient ? " (Insufficient)" : ""}
                          </option>
                        );
                      })}
                  </select>
                  {financeData.budget_code &&
                    (() => {
                      const selectedBudget = budgets.find(
                        (b) => (b.code || b.id) === financeData.budget_code,
                      );
                      if (selectedBudget) {
                        const balance = parseFloat(
                          selectedBudget.available_amount || 0,
                        );
                        const budgeted = parseFloat(
                          selectedBudget.budgeted_amount || 0,
                        );
                        const spent = budgeted - balance;
                        const requestAmount = parseFloat(
                          selectedRequest?.amount || 0,
                        );

                        return (
                          <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm">
                            <div className="flex justify-between mb-1">
                              <span className="text-gray-600">
                                Total Budget:
                              </span>
                              <span className="font-medium">
                                ₦{budgeted.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between mb-1">
                              <span className="text-gray-600">Spent:</span>
                              <span className="font-medium text-orange-600">
                                ₦{spent.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between mb-1 font-semibold">
                              <span className="text-gray-700">Available:</span>
                              <span
                                className={
                                  balance >= requestAmount
                                    ? "text-green-600"
                                    : "text-red-600"
                                }
                              >
                                ₦{balance.toLocaleString()}
                              </span>
                            </div>
                            <div className="flex justify-between pt-2 border-t border-blue-300">
                              <span className="text-gray-600">
                                Request Amount:
                              </span>
                              <span className="font-medium">
                                ₦{requestAmount.toLocaleString()}
                              </span>
                            </div>

                            {/* Transactions List */}
                            {selectedBudget.transactions &&
                              selectedBudget.transactions.length > 0 && (
                                <div className="mt-3 pt-3 border-t border-blue-300">
                                  <h4 className="font-semibold text-xs text-gray-700 mb-2">
                                    Recent Transactions (
                                    {selectedBudget.transactions.length})
                                  </h4>
                                  <div className="max-h-32 overflow-y-auto space-y-1.5">
                                    {selectedBudget.transactions
                                      .slice(0, 5)
                                      .map((txn, idx) => (
                                        <div
                                          key={idx}
                                          className="text-xs bg-white p-2 rounded border border-blue-100"
                                        >
                                          <div className="flex justify-between items-start">
                                            <span className="text-gray-700 flex-1">
                                              {txn.description ||
                                                txn.reference ||
                                                "Purchase Request"}
                                            </span>
                                            <span className="font-semibold text-red-600 ml-2">
                                              -₦
                                              {parseFloat(
                                                txn.amount || 0,
                                              ).toLocaleString()}
                                            </span>
                                          </div>
                                          <div className="text-gray-500 text-xs mt-0.5">
                                            {new Date(
                                              txn.created_at || txn.date,
                                            ).toLocaleDateString()}
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              )}

                            {balance < requestAmount && (
                              <div className="mt-2 text-red-600 font-medium text-xs">
                                ⚠️ Insufficient budget balance!
                              </div>
                            )}
                          </div>
                        );
                      }
                    })()}
                </div>
              )}

              {/* Budget Amount Display - REMOVED, now shown in budget selector */}
              {false &&
                financeData.action === "approve" &&
                financeData.budget_amount && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Budget Amount
                    </label>
                    <div className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-900">
                      ₦
                      {parseFloat(
                        financeData.budget_amount || 0,
                      ).toLocaleString()}
                    </div>
                  </div>
                )}

              {/* Payment Method (for approval) */}
              {financeData.action === "approve" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Method
                  </label>
                  <select
                    value={financeData.payment_method}
                    onChange={(e) =>
                      setFinanceData((prev) => ({
                        ...prev,
                        payment_method: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="check">Check</option>
                    <option value="cash">Cash</option>
                    <option value="wire_transfer">Wire Transfer</option>
                  </select>
                </div>
              )}

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {financeData.action === "approve"
                    ? "Approval Comments"
                    : "Rejection Reason"}{" "}
                  *
                </label>
                <textarea
                  value={financeData.comments}
                  onChange={(e) =>
                    setFinanceData((prev) => ({
                      ...prev,
                      comments: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={
                    financeData.action === "approve"
                      ? "Enter approval comments..."
                      : "Enter reason for budget rejection..."
                  }
                />
              </div>

              {/* Finance Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Finance Notes
                </label>
                <textarea
                  value={financeData.finance_notes}
                  onChange={(e) =>
                    setFinanceData((prev) => ({
                      ...prev,
                      finance_notes: e.target.value,
                    }))
                  }
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Additional finance notes (optional)..."
                />
              </div>

              {/* Action Impact */}
              <div
                className={`p-3 rounded border ${
                  financeData.action === "approve"
                    ? "bg-green-50 border-green-200"
                    : "bg-red-50 border-red-200"
                }`}
              >
                <p
                  className={`text-sm font-medium ${
                    financeData.action === "approve"
                      ? "text-green-800"
                      : "text-red-800"
                  }`}
                >
                  {financeData.action === "approve"
                    ? "✓ This will:"
                    : "⚠️ This will:"}
                </p>
                <ul
                  className={`text-sm mt-1 ml-4 list-disc ${
                    financeData.action === "approve"
                      ? "text-green-700"
                      : "text-red-700"
                  }`}
                >
                  {financeData.action === "approve" ? (
                    <>
                      <li>Approve budget allocation for this request</li>
                      <li>Authorize payment processing</li>
                      <li>Move request to payment processing queue</li>
                      <li>
                        Assign budget code:{" "}
                        {financeData.budget_code || "[Budget Code]"}
                      </li>
                    </>
                  ) : (
                    <>
                      <li>Reject the budget request</li>
                      <li>Stop the approval workflow</li>
                      <li>Notify requester of budget rejection</li>
                      <li>Request will need to be resubmitted</li>
                    </>
                  )}
                </ul>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              <button
                onClick={() => {
                  setShowFinanceModal(false);
                  setSelectedRequest(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitFinanceAction}
                disabled={processingId === selectedRequest.request_id}
                className={`px-4 py-2 rounded-md text-white focus:outline-none focus:ring-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  financeData.action === "approve"
                    ? "bg-green-600 hover:bg-green-700 focus:ring-green-500"
                    : "bg-red-600 hover:bg-red-700 focus:ring-red-500"
                }`}
              >
                {processingId === selectedRequest.request_id
                  ? "Processing..."
                  : financeData.action === "approve"
                    ? "Approve & Authorize Payment"
                    : "Reject Budget"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Payment Processing Modal */}
      {showPaymentModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Pay Vendor - Download Payment Letter
              </h3>
              <div className="flex items-center space-x-3">
                <button
                  onClick={handlePreviewPaymentLetter}
                  disabled={!validateTemplate().valid}
                  className="flex items-center space-x-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  <Eye className="h-4 w-4" />
                  <span>Preview</span>
                </button>
                <button
                  onClick={() => {
                    setShowPaymentModal(false);
                    setSelectedRequest(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-medium text-blue-900 mb-3">
                Payment Summary (Read-Only)
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Request ID
                    </label>
                    <p className="font-semibold text-gray-900">
                      {selectedRequest.request_id}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Vendor/Payee Name
                    </label>
                    <p className="font-semibold text-gray-900">
                      {selectedRequest.vendor_name}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Amount (Read-Only)
                    </label>
                    <p className="font-bold text-green-600 text-lg">
                      {formatCurrency(selectedRequest.amount)}
                    </p>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Requester
                    </label>
                    <p className="font-semibold text-gray-900">
                      {selectedRequest.requester_name || "Finance Department"}
                    </p>
                  </div>
                  <div>
                    <label className="text-gray-600 text-xs block mb-1">
                      Payment Method
                    </label>
                    {selectedRequest.payment_method === "cash" ? (
                      <div className="bg-green-100 p-2 rounded border border-green-200">
                        <p className="font-semibold text-green-800 text-sm">
                          💰 CASH PAYMENT
                        </p>
                        <p className="text-green-700 text-xs">
                          No bank details required
                        </p>
                      </div>
                    ) : (
                      <div className="bg-blue-100 p-2 rounded border border-blue-200">
                        <p className="font-semibold text-blue-800 text-sm">
                          🏦 BANK TRANSFER
                        </p>
                        <p className="text-blue-700 text-xs">
                          Bank details below
                        </p>
                      </div>
                    )}
                  </div>
                  {selectedRequest.payment_method !== "cash" && (
                    <div>
                      <label className="text-gray-600 text-xs block mb-1">
                        Bank Details (Read-Only)
                      </label>
                      <div className="bg-white p-2 rounded border border-gray-300">
                        <p className="font-mono text-xs text-gray-900 whitespace-pre-wrap">
                          {selectedRequest.vendor_bank_details ||
                            "Not provided"}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Letterhead Selection - COMMENTED OUT - Print on pre-printed letterhead */}
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Letterhead *
                </label>
                <select
                  value={paymentData.letterhead}
                  onChange={(e) =>
                    setPaymentData((prev) => ({
                      ...prev,
                      letterhead: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {letterheads.map((lh) => (
                    <option key={lh.value} value={lh.value}>
                      {lh.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose the company letterhead for the payment document
                </p>
              </div> */}

              {/* Document Template Editor */}
              <div>
                <div className="mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Payment Letter Content
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    This letter will be printed on pre-printed company
                    letterhead.
                  </p>
                </div>
                <textarea
                  value={paymentData.document_template}
                  onChange={(e) =>
                    setPaymentData((prev) => ({
                      ...prev,
                      document_template: e.target.value,
                    }))
                  }
                  rows={12}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="The default template will be loaded automatically. You can edit it as needed."
                />

                {/* Validation Warning */}
                {!validateTemplate().valid && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-800 font-medium flex items-center">
                      <span className="mr-2">⚠️</span>
                      {validateTemplate().message}
                    </p>
                    <p className="text-xs text-red-700 mt-1">
                      You cannot download the PDF or submit payment until all
                      protected placeholders are restored.
                    </p>
                  </div>
                )}

                <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                  <p className="text-xs text-yellow-800 font-medium">
                    ⚠️ Protected Information (Auto-filled in PDF - Cannot be
                    edited):
                  </p>
                  <ul className="text-xs text-yellow-700 mt-1 ml-4 list-disc space-y-1">
                    <li>
                      <strong>Amount:</strong> N
                      {selectedRequest?.amount
                        ? parseFloat(selectedRequest.amount).toLocaleString(
                            "en-NG",
                            {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            },
                          )
                        : "0.00"}
                    </li>
                    {selectedRequest?.payment_method !== "cash" ? (
                      <li>
                        <strong>Bank Details:</strong>{" "}
                        {selectedRequest?.vendor_bank_details || "Not provided"}
                      </li>
                    ) : (
                      <li>
                        <strong>Payment Type:</strong> CASH PAYMENT (No bank
                        details)
                      </li>
                    )}
                  </ul>
                  <p className="text-xs text-yellow-700 mt-2">
                    These values are automatically inserted into the letter and
                    cannot be modified.
                  </p>
                </div>
              </div>

              {/* Next Step Info */}
              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm font-medium text-blue-800">
                  ℹ️ After downloading the payment letter:
                </p>
                <ul className="text-sm text-blue-700 mt-1 ml-4 list-disc">
                  <li>Process the payment through your bank</li>
                  <li>Request will move to "Confirm Payment" tab</li>
                  <li>Enter payment reference number in the next stage</li>
                  <li>Complete final payment confirmation</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              {/* Debug Info - Remove after testing */}
              <div className="flex-1 text-sm text-gray-600">
                {(() => {
                  const validation = validateTemplate();
                  const isProcessing =
                    processingId === selectedRequest?.request_id;
                  return (
                    <div>
                      <div>
                        Validation:{" "}
                        {validation.valid
                          ? "✅ Valid"
                          : `❌ ${validation.message}`}
                      </div>
                      <div>Processing: {isProcessing ? "⏳ Yes" : "✅ No"}</div>
                      <div>
                        Button Status:{" "}
                        {isProcessing || !validation.valid
                          ? "🔒 Disabled"
                          : "✅ Enabled"}
                      </div>
                    </div>
                  );
                })()}
              </div>

              <button
                onClick={() => {
                  setShowPaymentModal(false);
                  setSelectedRequest(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  console.log("🔘 Process Payment button clicked!");
                  console.log("🔘 selectedRequest:", selectedRequest);
                  console.log("🔘 processingId:", processingId);
                  handleSubmitPayment();
                }}
                disabled={
                  processingId === selectedRequest?.request_id ||
                  !validateTemplate().valid
                }
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                title={
                  !validateTemplate().valid
                    ? "Please restore all protected placeholders before submitting"
                    : ""
                }
              >
                {processingId === selectedRequest.request_id ? (
                  <span>Processing...</span>
                ) : (
                  <>
                    <Download className="h-4 w-4" />
                    <span>Process Payment & Download Letter</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Payment Completion Modal */}
      {showConfirmPaymentModal && selectedRequest && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Confirm Payment Completion
              </h3>
              <button
                onClick={() => {
                  setShowConfirmPaymentModal(false);
                  setSelectedRequest(null);
                  setConfirmPaymentData({
                    payment_reference: "",
                    transaction_id: "",
                    payment_date: new Date().toISOString().split("T")[0],
                    payment_method: "bank_transfer",
                    comments: "",
                  });
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <h4 className="font-medium text-blue-900 mb-3">
                Request Summary
              </h4>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-gray-600 text-xs">Request ID</label>
                  <p className="font-semibold text-gray-900">
                    {selectedRequest.request_id}
                  </p>
                </div>
                <div>
                  <label className="text-gray-600 text-xs">Vendor Name</label>
                  <p className="font-semibold text-gray-900">
                    {selectedRequest.vendor_name}
                  </p>
                </div>
                <div>
                  <label className="text-gray-600 text-xs">Amount</label>
                  <p className="font-bold text-green-600 text-lg">
                    {formatCurrency(selectedRequest.amount)}
                  </p>
                </div>
                <div>
                  <label className="text-gray-600 text-xs">Requester</label>
                  <p className="font-semibold text-gray-900">
                    {selectedRequest.requester_name}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {/* Payment Reference - REQUIRED */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Reference Number *
                </label>
                <input
                  type="text"
                  value={confirmPaymentData.payment_reference}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      payment_reference: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter payment reference number from bank"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the reference number provided by your bank after payment
                </p>
              </div>

              {/* Transaction ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transaction ID (Optional)
                </label>
                <input
                  type="text"
                  value={confirmPaymentData.transaction_id}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      transaction_id: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter transaction ID (optional)"
                />
              </div>

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Date *
                </label>
                <input
                  type="date"
                  value={confirmPaymentData.payment_date}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      payment_date: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                />
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method *
                </label>
                <select
                  value={confirmPaymentData.payment_method}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      payment_method: e.target.value,
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="check">Check</option>
                  <option value="cash">Cash</option>
                  <option value="wire_transfer">Wire Transfer</option>
                </select>
              </div>

              {/* Comments */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Confirmation Comments
                </label>
                <textarea
                  value={confirmPaymentData.comments}
                  onChange={(e) =>
                    setConfirmPaymentData((prev) => ({
                      ...prev,
                      comments: e.target.value,
                    }))
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                  placeholder="Enter any additional comments about the payment..."
                />
              </div>

              {/* Completion Impact */}
              <div className="bg-green-50 border border-green-200 rounded p-3">
                <p className="text-sm font-medium text-green-800">
                  ✓ Confirming payment will:
                </p>
                <ul className="text-sm text-green-700 mt-1 ml-4 list-disc">
                  <li>Mark payment as completed</li>
                  <li>Move request to delivery confirmation stage</li>
                  <li>Notify operations team for delivery tracking</li>
                  <li>Update financial records with payment details</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 mt-6">
              <button
                onClick={() => {
                  setShowConfirmPaymentModal(false);
                  setSelectedRequest(null);
                  setConfirmPaymentData({
                    payment_reference: "",
                    transaction_id: "",
                    payment_date: new Date().toISOString().split("T")[0],
                    payment_method: "bank_transfer",
                    comments: "",
                  });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!confirmPaymentData.payment_reference.trim()) {
                    toast.error("Payment reference number is required");
                    return;
                  }

                  try {
                    setProcessingId(
                      selectedRequest.request_id || selectedRequest.id,
                    );

                    if (selectedRequest.isTransaction) {
                      // For transactions: just show success, transaction stays visible
                      toast.success(
                        "Transaction payment confirmed successfully!",
                      );
                    } else {
                      await purchaseRequestService.confirmPayment(
                        selectedRequest.request_id,
                        confirmPaymentData,
                      );
                      toast.success("Payment confirmed successfully!");
                    }

                    setShowConfirmPaymentModal(false);
                    setSelectedRequest(null);
                    setConfirmPaymentData({
                      payment_reference: "",
                      transaction_id: "",
                      payment_date: new Date().toISOString().split("T")[0],
                      payment_method: "bank_transfer",
                      comments: "",
                    });

                    await fetchFinanceRequests();
                  } catch (error) {
                    console.error("Confirm payment error:", error);
                    toast.error(
                      error.response?.data?.message ||
                        "Failed to confirm payment",
                    );
                  } finally {
                    setProcessingId(null);
                  }
                }}
                disabled={
                  processingId ===
                    (selectedRequest.request_id || selectedRequest.id) ||
                  !confirmPaymentData.payment_reference.trim()
                }
                className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {processingId ===
                (selectedRequest.request_id || selectedRequest.id) ? (
                  <span>Processing...</span>
                ) : (
                  <span>Confirm Payment Completion</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Receipt Modal */}
      {showReceipt && selectedRequest && (
        <PurchaseReceipt
          request={selectedRequest}
          onClose={() => {
            setShowReceipt(false);
            setSelectedRequest(null);
          }}
        />
      )}

      {/* Payment Letter Preview Modal */}
      {showPreviewModal && previewUrl && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div
            className="relative top-10 mx-auto p-5 border w-full max-w-5xl shadow-lg rounded-md bg-white"
            style={{ height: "90vh" }}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Payment Letter Preview
              </h3>
              <button
                onClick={() => {
                  setShowPreviewModal(false);
                  URL.revokeObjectURL(previewUrl);
                  setPreviewUrl(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="h-full pb-16">
              <iframe
                src={previewUrl}
                className="w-full h-full border border-gray-300 rounded"
                title="Payment Letter Preview"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancePayment;
